import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const MediterraneanADUDesignMilpitas = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Mediterranean ADU Design & Construction in Milpitas",
    "description": "Custom Mediterranean ADUs in Milpitas. Expert design & construction by UDA ADU. Authentic Mediterranean architecture with modern amenities.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU"
    },
    "url": "https://www.udaadu.com/mediterranean-adu-design-milpitas"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/mediterranean-adu-design-milpitas" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/30min"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Mediterranean ADU Design & Construction in Milpitas
          </h1>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Transform your Milpitas property with a stunning Mediterranean ADU that captures the essence of coastal elegance. Our expert team delivers authentic Mediterranean architecture with modern functionality and energy efficiency.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Free Design Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/landing">Try UDA AI Designer</Link>
            </Button>
          </div>
        </div>

        {/* Mediterranean Features Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Authentic Mediterranean ADU Features
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Terra Cotta Tile Roofing</h3>
                  <p className="text-gray-200">Classic red clay tiles that provide authenticity and excellent insulation</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Stucco Exterior Walls</h3>
                  <p className="text-gray-200">Textured stucco in warm earth tones characteristic of Mediterranean design</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Arched Windows & Doorways</h3>
                  <p className="text-gray-200">Elegant arches that create visual interest and Old World charm</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Wrought Iron Details</h3>
                  <p className="text-gray-200">Decorative ironwork on windows, balconies, and fixtures</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Outdoor Living Spaces</h3>
                  <p className="text-gray-200">Courtyards, patios, and terraces for indoor-outdoor living</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Milpitas Mediterranean ADU Benefits
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Climate-Perfect Design</h3>
                <p className="text-muted-foreground">Mediterranean architecture naturally suits Milpitas's mild climate with excellent thermal performance</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Increased Property Value</h3>
                <p className="text-muted-foreground">Mediterranean ADUs command premium rents and add significant home value in Milpitas</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Timeless Appeal</h3>
                <p className="text-muted-foreground">Classic Mediterranean design never goes out of style, ensuring lasting investment value</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-white">
            Mediterranean ADU Design Process in Milpitas
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Design Consultation</h3>
              <p className="text-gray-200">Discuss your Mediterranean vision and site requirements</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Architectural Design</h3>
              <p className="text-gray-200">Create custom Mediterranean plans for your Milpitas property</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Permit Approval</h3>
              <p className="text-gray-200">Navigate Milpitas building codes and secure permits</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Construction</h3>
              <p className="text-gray-200">Expert craftmanship bringing your Mediterranean ADU to life</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-3xl font-semibold mb-4 text-foreground">
            Ready to Build Your Mediterranean ADU in Milpitas?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Transform your property with an authentic Mediterranean ADU that combines Old World charm with modern convenience. Our Milpitas experts are ready to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Book Your Free Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/adu-calculator">Get Cost Estimate</Link>
            </Button>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4 text-white">Explore More ADU Designs</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contemporary-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Contemporary ADUs Milpitas
            </Link>
            <Link to="/spanish-colonial-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Spanish Colonial ADUs Milpitas
            </Link>
            <Link to="/craftsman-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Craftsman ADUs Milpitas
            </Link>
            <Link to="/modern-farmhouse-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Modern Farmhouse ADUs Milpitas
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default MediterraneanADUDesignMilpitas;